import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Plus, X } from 'lucide-react'
import { SOCIAL_NOTE_MAX_LENGTH } from '@digital-twins/shared-types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Text } from '@/components/ui/Text'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { TrackerTopicsStepProps } from '@/types/components/social-tracker-dialog'
import { addSocialTerms } from './socialTrackerForm'

export function SocialTrackerTopicsStep({
  form,
  updateField,
  drafts,
  updateDraft,
  activeKeyword,
  onActiveKeywordChange,
  fieldError,
  missing,
  isBusy,
}: TrackerTopicsStepProps) {
  const { t } = useTranslation('app')
  const inputRef = useRef<HTMLInputElement>(null)
  const selected = form.keywords.includes(activeKeyword ?? '')
    ? activeKeyword
    : (form.keywords[0] ?? null)
  const description =
    form.keywordNotes.find((note) => note.keyword === selected)?.description ??
    ''
  function commit() {
    const values = addSocialTerms(form.keywords, drafts.keywords)
    updateField('keywords', values)
    updateDraft('keywords', '')
    const added = values.find((value) => !form.keywords.includes(value))
    if (added) onActiveKeywordChange(added)
  }
  function remove(keyword: string) {
    const index = form.keywords.indexOf(keyword)
    const values = form.keywords.filter((value) => value !== keyword)
    updateField('keywords', values)
    updateField(
      'keywordNotes',
      form.keywordNotes.filter((note) => note.keyword !== keyword),
    )
    if (selected === keyword)
      onActiveKeywordChange(values[Math.min(index, values.length - 1)] ?? null)
    inputRef.current?.focus()
  }
  return (
    <div
      className="flex flex-col gap-5"
      data-testid="social-tracker-topics-step"
    >
      <div className="overflow-hidden rounded-xl border border-border bg-card-inset focus-within:border-primary/45">
        <fieldset
          disabled={isBusy}
          className="flex min-w-0 flex-col gap-2 px-3 py-2"
        >
          <legend className="sr-only">
            {t('socialListening.wizard.topics')}
          </legend>
          {form.keywords.length ? (
            <ul
              className="flex max-h-36 flex-wrap gap-2 overflow-y-auto overscroll-contain p-1"
              aria-label={t('socialListening.wizard.topics')}
              data-testid="social-tracker-keywords-input-values"
            >
              {form.keywords.map((keyword) => {
                const hasMeaning = Boolean(
                  form.keywordNotes
                    .find((note) => note.keyword === keyword)
                    ?.description.trim(),
                )
                return (
                  <li
                    key={keyword}
                    className={cn(
                      'inline-flex max-w-full items-center rounded-lg border text-sm transition-colors',
                      selected === keyword
                        ? 'border-primary/40 bg-primary/10 text-foreground'
                        : 'border-border bg-background text-muted-foreground',
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={selected === keyword}
                      aria-controls="social-topic-meaning"
                      data-testid={`social-tracker-select-${keyword}`}
                      className="flex min-w-0 items-center gap-1.5 rounded-l-lg px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => onActiveKeywordChange(keyword)}
                    >
                      <span className="truncate">{keyword}</span>
                      {hasMeaning ? (
                        <Check
                          className="h-3 w-3 shrink-0"
                          aria-label={t('socialListening.wizard.meaningAdded')}
                        />
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className="shrink-0 rounded-r-lg p-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={t('socialListening.form.removeTermAria', {
                        term: keyword,
                      })}
                      data-testid={`social-tracker-keywords-input-remove-${keyword}`}
                      onClick={() => remove(keyword)}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : null}
          <Label htmlFor="social-tracker-keywords" className="sr-only">
            {t('socialListening.wizard.topics')}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              id="social-tracker-keywords"
              value={drafts.keywords}
              className="h-10 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              placeholder={t('socialListening.wizard.topicPlaceholder')}
              aria-invalid={missing.includes('keywords') || undefined}
              aria-describedby="social-tracker-topics-help"
              data-testid="social-tracker-keywords-input"
              onChange={(event) => updateDraft('keywords', event.target.value)}
              onKeyDown={(event) => {
                if (event.nativeEvent.isComposing) return
                if (event.key === 'Enter' || event.key === ',') {
                  event.preventDefault()
                  commit()
                }
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={!drafts.keywords.trim()}
              data-testid="social-tracker-topic-commit"
              aria-label={t('socialListening.wizard.addSearchTopic')}
              onClick={commit}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </fieldset>
        {selected ? (
          <div
            id="social-topic-meaning"
            className="flex flex-col gap-2 border-t border-border bg-background/50 p-4 sm:p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <Label
                htmlFor="social-tracker-meaning"
                className="min-w-0 truncate"
              >
                {t('socialListening.wizard.meaningFor', { term: selected })}
              </Label>
              <Text as="span" size="xs" color="muted">
                {t('socialListening.wizard.optional')}
              </Text>
            </div>
            <Textarea
              id="social-tracker-meaning"
              data-testid={`social-tracker-note-${selected}`}
              rows={3}
              maxLength={SOCIAL_NOTE_MAX_LENGTH}
              value={description}
              disabled={isBusy}
              className="resize-none border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              placeholder={t('socialListening.wizard.meaningPlaceholder')}
              onChange={(event) =>
                updateField('keywordNotes', [
                  ...form.keywordNotes.filter(
                    (note) => note.keyword !== selected,
                  ),
                  { keyword: selected, description: event.target.value },
                ])
              }
            />
            <Text as="span" size="xs" color="muted" className="self-end">
              {description.length}/{SOCIAL_NOTE_MAX_LENGTH}
            </Text>
          </div>
        ) : null}
      </div>
      <Text size="sm" color="muted" id="social-tracker-topics-help">
        {t('socialListening.wizard.topicsHelp')}
      </Text>
      {fieldError('keywords')}
    </div>
  )
}
